import { CCI_FEATURE_ENABLED, shouldUseCciPath } from './shouldUseCciPath';
import { generateCciBom } from './generateCciBom';
import { isDualCpuFlashQuote } from './flashNodeModel';

/**
 * Decide which path a quote generation takes, and describe that decision for the
 * audit log. Extracted from App.js so it can be unit-tested; App.js has no working
 * test suite.
 *
 * Never throws from CCI business logic: a CCI failure of any kind degrades to the
 * standard path, because quote generation must not break just because the CCI
 * bundle could not be built. A missing `prices` argument is a caller bug, not a
 * business outcome, and does throw — see the guard below.
 *
 * @param {PriceResolver} prices - Resolved price source (src/pricing/priceResolver.ts).
 *   REQUIRED, no default: see src/pricing/priceResolver.ts for why omission must throw.
 * @returns {{quotePath: 'cci'|'cci_fallback'|'standard', cciResult: object|null, auditFields: object}}
 */
export function resolveCciQuotePath({
  vendorId,
  cciEnabled,
  hasSpecialAccess,
  config,
  productDb,
  metrics,
  pricingData,
  vendor,
  prices,
}) {
  if (typeof prices?.of !== 'function') {
    throw new TypeError('resolveCciQuotePath requires a PriceResolver.');
  }
  // CONF-210: no CCI bundle covers the Dual CPU flash node. A quote that ships
  // one takes the standard path whatever the toggle says, and the audit log
  // records why — the UI disables the toggle with the same reason, so this is
  // the backstop for a quote saved with cciEnabled before the swap.
  const dualCpuFlash = isDualCpuFlashQuote(config);
  if (CCI_FEATURE_ENABLED && dualCpuFlash && vendorId === 'AIC' && cciEnabled === true) {
    return {
      quotePath: 'standard',
      cciResult: null,
      auditFields: { quotePath: 'standard', cciReason: 'dual_cpu_flash' },
    };
  }
  if (!CCI_FEATURE_ENABLED || !shouldUseCciPath({ vendorId, cciEnabled, hasSpecialAccess, dualCpuFlash })) {
    return { quotePath: 'standard', cciResult: null, auditFields: { quotePath: 'standard' } };
  }

  let cciResult;
  try {
    cciResult = generateCciBom(config, productDb, metrics, pricingData, vendor, prices);
  } catch (error) {
    // generateCciBom calls generateHwBom, which is not total across all configs.
    console.error('CCI BOM generation threw:', error);
    cciResult = { success: false, reason: 'CCI BOM generation failed.' };
  }

  if (cciResult.success) {
    return { quotePath: 'cci', cciResult, auditFields: { quotePath: 'cci' } };
  }

  return {
    quotePath: 'cci_fallback',
    cciResult,
    auditFields: { quotePath: 'cci_fallback', cciReason: cciResult.reason },
  };
}
